import { atom, createStore } from "jotai";
import {
  getFeatureFlagDefaults,
  type FeatureFlagState,
  type FeatureFlagLocalKey,
} from "~/feature-flags-defaults";
import {
  applyLocalOverrideFeatureFlags,
  FEATURE_FLAGS_STORAGE_KEY,
} from "~/utils/feature-flags-utils";

export const featureFlagStore = createStore();

/**
 * Written directly only by an impersonated session, whose flags belong to the
 * impersonated user and must not overwrite the staff user's stored overrides.
 */
export const unpersistedFeatureFlagsAtom = atom<FeatureFlagState>(
  applyLocalOverrideFeatureFlags(getFeatureFlagDefaults()),
);

/**
 * Persisted storage for FeatureFlag Administration.
 */
export const featureFlagsAtom = atom<
  FeatureFlagState,
  [Partial<FeatureFlagState>],
  FeatureFlagState
>(
  (get) => get(unpersistedFeatureFlagsAtom),
  (get, set, updates) => {
    const currentState = get(unpersistedFeatureFlagsAtom);

    const updatedState = {
      ...currentState,
      ...updates,
    };

    set(unpersistedFeatureFlagsAtom, updatedState);

    // Load current persisted overrides.
    const json = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    const persistedValue = (
      json ? JSON.parse(json) : {}
    ) as Partial<FeatureFlagState>;

    const updatedPersistValue = {
      ...persistedValue,
      ...updates,
    };

    const cleanedPersistValue = Object.entries(updatedPersistValue).reduce<
      Partial<FeatureFlagState>
    >((cleaned, [key, value]) => {
      const defaults = getFeatureFlagDefaults();
      const k = key as FeatureFlagLocalKey;
      if (defaults[k] !== updatedPersistValue[k]) {
        cleaned[k] = value;
      }
      return cleaned;
    }, {});

    // Persist data
    if (Object.keys(cleanedPersistValue).length) {
      localStorage.setItem(
        FEATURE_FLAGS_STORAGE_KEY,
        JSON.stringify(cleanedPersistValue),
      );
    } else {
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    }

    return updatedState;
  },
);
